import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import { InputAdornment } from "@mui/material";
import MessageBlock from "components/shared/MessageBlock";
import { Spinner } from "components/shared/Spinner";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { TFunction } from "i18next";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { DocoonSignatory, DocoonSignatoryWithRole } from "store/agreementsStore";

// ─── Helpers ────────────────────────────────────────────────────────────────

function useProgressiveMessage(active: boolean, messages: string[], firstDelay = 5000, step = 8000) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (!active) {
            setIndex(0);
            return;
        }
        const timers = messages.slice(1).map((_, i) => setTimeout(() => setIndex(i + 1), firstDelay + i * step));
        return () => timers.forEach(clearTimeout);
    }, [active]);

    return messages[Math.min(index, messages.length - 1)];
}

type SignatoryGroup = { email: string; signatories: DocoonSignatory[] };

function groupByEmail(list: DocoonSignatory[]): SignatoryGroup[] {
    const map = new Map<string, DocoonSignatory[]>();
    for (const s of list) {
        const key = s.email.toLowerCase();
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(s);
    }
    return Array.from(map.entries()).map(([, sigs]) => ({ email: sigs[0].email, signatories: sigs }));
}

function filterGroups(groups: SignatoryGroup[], query: string): SignatoryGroup[] {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return groups
        .map((g) => ({
            ...g,
            signatories: g.signatories.filter(
                (s) =>
                    s.email.toLowerCase().includes(q) ||
                    s.firstName.toLowerCase().includes(q) ||
                    s.lastName.toLowerCase().includes(q),
            ),
        }))
        .filter((g) => g.signatories.length > 0);
}

function getModeLabel(modeSecure: string | null | undefined, t: TFunction): string | null {
    if (!modeSecure) return null;
    return modeSecure.includes("SMS") ? t("DocoonSignModal.VerifSms") : t("DocoonSignModal.VerifEmail");
}

function getIdentityLabel(s: DocoonSignatory): string | null {
    if (s.identType && s.identCode) return `${s.identType} · ${s.identCode}`;
    if (s.identType) return s.identType;
    return null;
}

function getInitials(s: DocoonSignatory): string {
    return ((s.lastName[0] ?? "") + (s.firstName[0] ?? "")).toUpperCase();
}

// ─── Shared sub-components ──────────────────────────────────────────────────

const ModeTag = ({ modeSecure, t }: { modeSecure?: string | null; t: TFunction }) => {
    const label = getModeLabel(modeSecure, t);
    if (!label) return null;
    return (
        <span className="text-[11px] text-[#275ab0] bg-[#eef3fd] rounded-full px-2.5 py-0.5 whitespace-nowrap flex-none">
            {label}
        </span>
    );
};

const Avatar = ({ s, isMain }: { s: DocoonSignatory; isMain: boolean }) => (
    <span
        className={`w-[34px] h-[34px] rounded-full text-[#646464] text-xs flex items-center justify-center flex-none ${
            isMain ? "bg-[rgba(184,174,233,0.35)]" : "bg-[rgba(184,174,233,0.2)]"
        }`}
    >
        {getInitials(s)}
    </span>
);

// ─── Ambiguous panel ─────────────────────────────────────────────────────────

const AmbiguousPanel = ({
    identities,
    onSelect,
    t,
}: {
    identities: DocoonSignatory[];
    onSelect: (s: DocoonSignatory) => void;
    t: TFunction;
}) => (
    <div className="border border-[#B8AEE9] rounded-[3px] bg-[#F6F4FC] overflow-hidden mt-1">
        <div className="flex items-start gap-2 px-4 py-3 border-b border-[#e4dff5]">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: "#6c5bbe", flexShrink: 0, marginTop: "1px" }} />
            <span className="text-xs text-[#4b4b4b]">
                {t("DocoonSignModal.AmbiguousHint", { count: identities.length }) as string}
            </span>
        </div>
        {identities.map((s, i) => (
            <div key={s.id}>
                {i > 0 && <div className="h-px bg-[#e4dff5]" />}
                <div
                    onClick={() => onSelect(s)}
                    className="flex items-center gap-3 px-4 py-3 bg-[#F6F4FC] cursor-pointer hover:bg-[#efebfa] transition-colors"
                >
                    <span className="w-4 h-4 rounded-full border border-[#B4B4B4] bg-white flex-none" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#141516]">
                            {s.firstName} {s.lastName}
                        </div>
                        <div className="text-xs text-[#646464]">{getIdentityLabel(s)}</div>
                    </div>
                    <ModeTag modeSecure={s.modeSecure} t={t} />
                </div>
            </div>
        ))}
    </div>
);

// ─── Dropdown ────────────────────────────────────────────────────────────────

const DropdownRow = ({
    s,
    isDisabled,
    onSelect,
    t,
}: {
    s: DocoonSignatory;
    isDisabled: boolean;
    onSelect: () => void;
    t: TFunction;
}) => (
    <div
        onClick={isDisabled ? undefined : onSelect}
        className={`flex items-center justify-between h-[54px] px-4 pl-7 transition-colors ${
            isDisabled ? "opacity-55 cursor-not-allowed bg-gray-50" : "cursor-pointer bg-white hover:bg-gray-100"
        }`}
    >
        <div className="flex items-center gap-2 min-w-0">
            <span className="w-[30px] h-[30px] rounded-full bg-[rgba(184,174,233,0.2)] text-[#646464] text-[10px] flex items-center justify-center flex-none">
                {getInitials(s)}
            </span>
            <div className="min-w-0">
                <div className="text-sm text-[#141516] leading-[18px]">
                    {s.lastName.toUpperCase()} {s.firstName}
                </div>
                <div className="text-xs text-[#646464] leading-4">{getIdentityLabel(s)}</div>
            </div>
        </div>
        {isDisabled ? (
            <span className="text-[11px] text-[#646464] bg-[#eaeaea] rounded-full px-2.5 py-0.5 flex-none">
                {t("DocoonSignModal.AlreadySignatory") as string}
            </span>
        ) : (
            <span className="text-xs text-[#646464] flex-none">{getModeLabel(s.modeSecure, t)}</span>
        )}
    </div>
);

const Dropdown = ({
    groups,
    selectedIds,
    onSelect,
    anchor,
    portalRef,
    isSearching,
    t,
}: {
    groups: SignatoryGroup[];
    selectedIds: Set<number>;
    onSelect: (s: DocoonSignatory) => void;
    anchor: HTMLElement | null;
    portalRef: (el: HTMLDivElement | null) => void;
    isSearching?: boolean;
    t: TFunction;
}) => {
    const searchMessage = useProgressiveMessage(!!isSearching, [
        t("DocoonSignModal.SearchMsg1"),
        t("DocoonSignModal.LoadingMsg2"),
        t("DocoonSignModal.LoadingMsg3"),
    ]);
    if (!anchor) return null;
    const { bottom, left, width } = anchor.getBoundingClientRect();
    return createPortal(
        <div
            ref={portalRef}
            style={{ position: "fixed", top: bottom + 4, left, width, zIndex: 9999 }}
            className="bg-white rounded-[5px] shadow-[0_0_20px_rgba(0,0,0,.12)] overflow-hidden max-h-[330px] overflow-y-auto"
        >
            {isSearching ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-[#646464]">
                    <div className="w-4 h-4 border-2 border-[#9185CB] border-t-transparent rounded-full flex-none animate-spin" />
                    {searchMessage}
                </div>
            ) : groups.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[#646464]">{t("DocoonSignModal.NoResults") as string}</div>
            ) : (
                groups.map((g) => (
                    <div key={g.email}>
                        <div className="flex items-center justify-between px-4 py-2 bg-[#fafafa] border-b border-[#eaeaea] sticky top-0">
                            <span className="text-xs font-semibold text-[#141516]">{g.email}</span>
                            {g.signatories.length > 1 && (
                                <span className="text-[11px] text-[#646464]">
                                    {t("DocoonSignModal.IdentitiesCount", { count: g.signatories.length }) as string}
                                </span>
                            )}
                        </div>
                        {g.signatories.map((s, i) => (
                            <div key={s.id}>
                                {i > 0 && <div className="h-px bg-[#eaeaea]" />}
                                <DropdownRow
                                    s={s}
                                    isDisabled={selectedIds.has(s.id)}
                                    onSelect={() => onSelect(s)}
                                    t={t}
                                />
                            </div>
                        ))}
                    </div>
                ))
            )}
        </div>,
        document.body,
    );
};

// ─── Search input ────────────────────────────────────────────────────────────

const SearchInput = ({
    value,
    onChange,
    onClear,
    onFocus,
    onBlur,
    autoFocus,
    placeholder,
    t,
}: {
    value: string;
    onChange: (v: string) => void;
    onClear: () => void;
    onFocus: () => void;
    onBlur?: () => void;
    autoFocus?: boolean;
    placeholder?: string;
    t: TFunction;
}) => (
    <StyledTextField
        fullWidth
        placeholder={placeholder ?? t("DocoonSignModal.SearchPlaceholder")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        autoComplete="off"
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "#9185CB" }} />
                </InputAdornment>
            ),
            endAdornment: value ? (
                <InputAdornment position="end">
                    <CloseIcon
                        onClick={onClear}
                        sx={{ fontSize: 18, color: "#B4B4B4", cursor: "pointer", "&:hover": { color: "#9B5067" } }}
                    />
                </InputAdornment>
            ) : undefined,
        }}
        sx={{
            "&.MuiTextField-root .MuiOutlinedInput-root": {
                "& > fieldset legend": { display: "none" },
                "& > fieldset": { top: 0 },
            },
        }}
    />
);

// ─── Selected card ───────────────────────────────────────────────────────────

const SelectedCard = ({
    s,
    isMain,
    allSignatories,
    selectedIds,
    onClear,
    onSelectAlternative,
    roleBadge,
    t,
}: {
    s: DocoonSignatory;
    isMain: boolean;
    allSignatories: DocoonSignatory[];
    selectedIds: Set<number>;
    onClear: () => void;
    onSelectAlternative?: (alt: DocoonSignatory) => void;
    // Role of an "other participant" (signatory or informed), shown next to the remove icon
    roleBadge?: ReactNode;
    t: TFunction;
}) => {
    const alternatives = isMain
        ? allSignatories.filter(
              (x) => x.email.toLowerCase() === s.email.toLowerCase() && x.id !== s.id && !selectedIds.has(x.id),
          )
        : [];

    return (
        <div
            className={`rounded-[3px] border p-4 ${
                isMain ? "border-[#9185CB] bg-[#F6F4FC]" : "border-[#eaeaea] bg-[#fafafa]"
            }`}
        >
            <div className="flex items-start gap-3">
                <Avatar s={s} isMain={isMain} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#141516]">
                            {s.firstName} {s.lastName}
                        </span>
                        <ModeTag modeSecure={s.modeSecure} t={t} />
                    </div>
                    <div className="text-xs text-[#646464]">{s.email}</div>
                </div>
                {isMain ? (
                    <div className="flex items-center gap-1.5 flex-none">
                        <span
                            onClick={onClear}
                            className="text-xs text-[#646464] cursor-pointer underline whitespace-nowrap"
                        >
                            {t("DocoonSignModal.Change") as string}
                        </span>
                    </div>
                ) : (
                    <div className="flex items-center gap-3 flex-none">
                        {roleBadge}
                        <CloseIcon
                            onClick={onClear}
                            sx={{ fontSize: 18, color: "#B4B4B4", cursor: "pointer", "&:hover": { color: "#9B5067" } }}
                        />
                    </div>
                )}
            </div>

            {isMain && alternatives.length > 0 && onSelectAlternative && (
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[#e4dff5]">
                    <SyncIcon sx={{ fontSize: 14, color: "#275ab0", flexShrink: 0 }} />
                    <span className="text-xs text-[#646464]">
                        {t("DocoonSignModal.AlternativeHint") as string}{" "}
                        {alternatives.map((alt, i) => (
                            <span key={alt.id}>
                                {i > 0 && ", "}
                                <button
                                    onClick={() => onSelectAlternative(alt)}
                                    className="text-[#275ab0] underline bg-transparent border-0 p-0 cursor-pointer text-xs"
                                >
                                    {
                                        (alt.identType
                                            ? t("DocoonSignModal.UseIdentity", {
                                                  label: `${alt.identType}${alt.identCode ? ` ${alt.identCode}` : ""}`,
                                              })
                                            : t("DocoonSignModal.UseNoIdentity")) as string
                                    }
                                </button>
                            </span>
                        ))}
                    </span>
                </div>
            )}
        </div>
    );
};

// ─── Remote search ───────────────────────────────────────────────────────────

const REMOTE_SEARCH_DEBOUNCE_MS = 500;
// Docoon's Email filter is a "contains" match, so short fragments of a name already hit the
// right address (e.g. "dupo" → jean.dupont@…); below this length results are too broad.
const REMOTE_SEARCH_MIN_LENGTH = 4;

type SearchFn = (query: string, signal?: AbortSignal) => Promise<DocoonSignatory[]>;

// Always queries Docoon (even on a local hit) so homonyms missing from the preloaded list still show up.
// Debounces input + aborts any in-flight request so a stale response can't overwrite the current one.
function useRemoteSearch(onSearch: SearchFn, onResults: (results: DocoonSignatory[]) => void) {
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const abort = useRef<AbortController>();
    const [isSearching, setIsSearching] = useState(false);

    const cancel = () => {
        clearTimeout(timer.current);
        abort.current?.abort();
    };

    useEffect(() => cancel, []);

    const search = (query: string, onEmpty?: () => void) => {
        cancel();
        const trimmed = query.trim();
        if (trimmed.length < REMOTE_SEARCH_MIN_LENGTH) {
            setIsSearching(false);
            return;
        }
        timer.current = setTimeout(async () => {
            const controller = new AbortController();
            abort.current = controller;
            setIsSearching(true);
            const results = await onSearch(trimmed, controller.signal);
            if (controller.signal.aborted) return;
            setIsSearching(false);
            if (!results.length) {
                onEmpty?.();
                return;
            }
            onResults(results);
        }, REMOTE_SEARCH_DEBOUNCE_MS);
    };

    return { isSearching, search };
}

// ─── Search field ────────────────────────────────────────────────────────────

const SignatorySearchField = ({
    query,
    onQueryChange,
    onClear,
    groups,
    selectedIds,
    onSelect,
    isSearching,
    hideDropdown,
    autoFocus,
    placeholder,
    onEmptyBlur,
    t,
    children,
}: {
    query: string;
    onQueryChange: (v: string) => void;
    onClear: () => void;
    groups: SignatoryGroup[];
    selectedIds: Set<number>;
    onSelect: (s: DocoonSignatory) => void;
    isSearching: boolean;
    hideDropdown?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
    // Called when the field loses the focus while empty (the dropdown only opens on a query)
    onEmptyBlur?: () => void;
    t: TFunction;
    children?: ReactNode;
}) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement | null>(null);

    // Close the floating dropdown on outside click (the dropdown lives in a portal, outside the wrapper)
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!wrapperRef.current?.contains(target) && !portalRef.current?.contains(target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        // The dropdown is positioned once from the input: close it when a parent scrolls it out of place
        const onScroll = (e: Event) => {
            if (!portalRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("scroll", onScroll, true);
        };
    }, []);

    return (
        <div ref={wrapperRef} className="relative">
            <SearchInput
                value={query}
                onChange={(v) => {
                    setOpen(true);
                    onQueryChange(v);
                }}
                onClear={() => {
                    setOpen(false);
                    onClear();
                }}
                onFocus={() => {
                    if (query) setOpen(true);
                }}
                onBlur={() => {
                    if (!query.trim()) onEmptyBlur?.();
                }}
                autoFocus={autoFocus}
                placeholder={placeholder}
                t={t}
            />
            {children}
            {!hideDropdown && open && query && (
                <Dropdown
                    groups={filterGroups(groups, query)}
                    selectedIds={selectedIds}
                    onSelect={onSelect}
                    anchor={wrapperRef.current}
                    portalRef={(el) => {
                        portalRef.current = el;
                    }}
                    isSearching={isSearching}
                    t={t}
                />
            )}
        </div>
    );
};

// A signatory searched from scratch, without a pre-filled email (additional and internal signatories)
const FreeSignatoryField = ({
    onSearch,
    onResults,
    onClear,
    ...fieldProps
}: Omit<Parameters<typeof SignatorySearchField>[0], "query" | "onQueryChange" | "isSearching"> & {
    onSearch: SearchFn;
    onResults: (results: DocoonSignatory[]) => void;
}) => {
    const [query, setQuery] = useState("");
    const { isSearching, search } = useRemoteSearch(onSearch, onResults);
    return (
        <SignatorySearchField
            {...fieldProps}
            query={query}
            onQueryChange={(v) => {
                setQuery(v);
                search(v);
            }}
            onClear={() => {
                setQuery("");
                search("");
                onClear();
            }}
            isSearching={isSearching}
        />
    );
};

// ─── Beneficiary slot ────────────────────────────────────────────────────────

// A signatory slot pre-filled with a beneficiary's email: auto-matches it against Docoon,
// lets the user pick among several identities sharing that email, or search manually.
const BeneficiarySignatorySlot = ({
    email,
    isMain,
    selected,
    onSelect,
    allSignatories,
    groups,
    selectedIds,
    claimedIds,
    onSearch,
    onResults,
    emailAlreadySearched,
    t,
}: {
    email: string;
    isMain: boolean;
    selected: DocoonSignatory | null;
    onSelect: (s: DocoonSignatory | null) => void;
    allSignatories: DocoonSignatory[];
    groups: SignatoryGroup[];
    selectedIds: Set<number>;
    claimedIds: Set<number>;
    onSearch: SearchFn;
    onResults: (results: DocoonSignatory[]) => void;
    // The parent already queried Docoon with this email: no match means not found, no need to search again
    emailAlreadySearched?: boolean;
    t: TFunction;
}) => {
    const [query, setQuery] = useState(email);
    const [ambiguous, setAmbiguous] = useState<DocoonSignatory[] | null>(null);
    const [emailNotFound, setEmailNotFound] = useState(false);
    const fallbackRef = useRef<string | null>(null);
    const remote = useRemoteSearch(onSearch, onResults);

    // Auto-match by email — either from the loaded list, or via the server-side fallback.
    useEffect(() => {
        if (selected || !email) return;
        const lower = email.toLowerCase();
        // Skip identities already picked in another slot (two beneficiaries may share an email)
        const matches = allSignatories.filter((s) => s.email.toLowerCase() === lower && !claimedIds.has(s.id));
        if (matches.length === 1) {
            claimedIds.add(matches[0].id);
            onSelect(matches[0]);
            setQuery(matches[0].email);
        } else if (matches.length > 1) {
            setQuery(email);
            setAmbiguous(matches);
        } else if (emailAlreadySearched && fallbackRef.current !== lower) {
            // Once per email: later list updates (the user's own searches) must not bring the warning back
            fallbackRef.current = lower;
            setEmailNotFound(true);
        } else if (
            !emailAlreadySearched &&
            email.includes("@") &&
            fallbackRef.current !== lower &&
            !remote.isSearching
        ) {
            fallbackRef.current = lower;
            remote.search(email, () => setEmailNotFound(true));
        }
    }, [allSignatories, email]);

    // Sync query when the email arrives asynchronously (e.g. after the parent finishes loading
    // the beneficiary). Only runs while nothing is selected and the user hasn't typed a query yet.
    useEffect(() => {
        if (selected || !email) return;
        setQuery((prev) => (prev ? prev : email));
    }, [email, selected]);

    const handleSelect = (s: DocoonSignatory) => {
        onSelect(s);
        setQuery(s.email);
        setAmbiguous(null);
        setEmailNotFound(false);
    };

    const handleClear = () => {
        onSelect(null);
        setQuery("");
        setAmbiguous(null);
        setEmailNotFound(false);
    };

    if (selected) {
        return (
            <SelectedCard
                s={selected}
                isMain={isMain}
                allSignatories={allSignatories}
                selectedIds={selectedIds}
                onClear={handleClear}
                onSelectAlternative={isMain ? handleSelect : undefined}
                t={t}
            />
        );
    }

    return (
        <div>
            <SignatorySearchField
                query={query}
                onQueryChange={(v) => {
                    setQuery(v);
                    setAmbiguous(null);
                    setEmailNotFound(false);
                    remote.search(v);
                }}
                onClear={handleClear}
                groups={groups}
                selectedIds={selectedIds}
                onSelect={handleSelect}
                isSearching={remote.isSearching}
                hideDropdown={!!ambiguous}
                t={t}
            >
                {!ambiguous && !remote.isSearching && emailNotFound && (
                    <div className="mt-2">
                        <MessageBlock type="warning" text={t("DocoonSignModal.EmailNotFound")} small />
                    </div>
                )}
            </SignatorySearchField>
            {ambiguous && <AmbiguousPanel identities={ambiguous} onSelect={handleSelect} t={t} />}
        </div>
    );
};

// ─── Addable entries (additional signatories, informed people) ──────────────

type ExtraEntry = { key: number; selected: DocoonSignatory | null };

type ParticipantRole = "signatory" | "informed";

const ROLE_BADGE: Record<ParticipantRole, { labelKey: string; className: string }> = {
    signatory: { labelKey: "DocoonSignModal.RoleSignatory", className: "text-[#141516] bg-[#f3f4f6]" },
    informed: { labelKey: "DocoonSignModal.RoleInformed", className: "text-[#275ab0] bg-[#eef3fd]" },
};

const EntryField = ({
    entry,
    onSelect,
    onRemove,
    allSignatories,
    groups,
    selectedIds,
    onSearch,
    onResults,
    role,
    autoFocus = true,
    t,
}: {
    entry: ExtraEntry;
    onSelect: (s: DocoonSignatory) => void;
    onRemove: () => void;
    allSignatories: DocoonSignatory[];
    groups: SignatoryGroup[];
    selectedIds: Set<number>;
    onSearch: SearchFn;
    onResults: (results: DocoonSignatory[]) => void;
    // An added participant: role badge and placeholder, and the field goes away if left empty
    role?: ParticipantRole;
    // Off for fields shown on open, so they don't steal the focus from the main signatory
    autoFocus?: boolean;
    t: TFunction;
}) =>
    entry.selected ? (
        <SelectedCard
            s={entry.selected}
            isMain={false}
            allSignatories={allSignatories}
            selectedIds={selectedIds}
            onClear={onRemove}
            roleBadge={
                role && (
                    <span
                        className={`text-[11px] rounded-full px-2.5 py-0.5 whitespace-nowrap ${ROLE_BADGE[role].className}`}
                    >
                        {t(ROLE_BADGE[role].labelKey) as string}
                    </span>
                )
            }
            t={t}
        />
    ) : (
        <FreeSignatoryField
            onSearch={onSearch}
            onResults={onResults}
            onClear={onRemove}
            groups={groups}
            selectedIds={selectedIds}
            onSelect={onSelect}
            autoFocus={autoFocus}
            placeholder={role === "informed" ? t("DocoonSignModal.SearchInformedPlaceholder") : undefined}
            onEmptyBlur={role ? onRemove : undefined}
            t={t}
        />
    );

// Says what the added person does, so the roles are understood without a tooltip
const AddEntryButton = ({
    enabled,
    icon,
    label,
    description,
    onClick,
}: {
    enabled: boolean;
    icon: ReactNode;
    label: string;
    description: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!enabled}
        className={`flex items-center gap-3 text-left rounded-[3px] border border-dashed bg-white px-4 py-3.5 min-h-[72px] font-[inherit] ${
            enabled
                ? "border-[#B8AEE9] cursor-pointer hover:bg-[#F6F4FC]"
                : "border-[#eaeaea] opacity-60 cursor-not-allowed"
        }`}
    >
        <span className="w-7 h-7 rounded-full bg-[#F6F4FC] text-[#6c5bbe] flex items-center justify-center flex-none">
            {icon}
        </span>
        <span className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-[#141516]">{label}</span>
            <span className="text-xs text-[#646464]">{description}</span>
        </span>
    </button>
);

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * Splits a picked signatory list back into the step's blocks, to re-open it pre-filled.
 * Signatories saved before roles were stored have none: they land in the additional block.
 */
export const toInitialSignatoryBlocks = (
    signatories: DocoonSignatoryWithRole[],
    coBeneficiaries: CoBeneficiarySigner[],
) => {
    const [initialMain, ...others] = signatories;
    const coBeneficiaryIds = new Set(coBeneficiaries.map((b) => b.beneficiaryId));
    const isCoSigner = (s: DocoonSignatoryWithRole) => s.beneficiaryId != null && coBeneficiaryIds.has(s.beneficiaryId);
    return {
        initialMain,
        initialCoSelections: Object.fromEntries(others.filter(isCoSigner).map((s) => [s.beneficiaryId, s])) as Record<
            number,
            DocoonSignatory
        >,
        initialInternal: others.find((s) => s.isInternal) ?? null,
        initialExtras: others.filter((s) => !isCoSigner(s) && !s.isInternal && !s.isInformed),
        initialInformed: others.filter((s) => s.isInformed),
    };
};

export type CoBeneficiarySigner = { beneficiaryId: number; email: string; name: string };

type Props = {
    mainSignerEmail: string;
    mainSignerName: string;
    docoonSignatories: DocoonSignatory[];
    isLoading: boolean;
    onSearch: SearchFn;
    onChange: (signatairesIds: string) => void;
    onSignatoriesChange?: (signatories: DocoonSignatoryWithRole[]) => void;
    initialMain?: DocoonSignatory;
    // Signatories already picked for each co-beneficiary, by beneficiary id (editing)
    initialCoSelections?: Record<number, DocoonSignatory>;
    initialInternal?: DocoonSignatory | null;
    initialExtras?: DocoonSignatory[];
    initialInformed?: DocoonSignatory[];
    // Other beneficiaries chosen for a multi-signature contract: one pre-filled slot each
    coBeneficiaries?: CoBeneficiarySigner[];
    // Why the selection can't be submitted yet (null when complete), e.g. for a disabled button tooltip
    onBlockingReasonChange?: (reason: string | null) => void;
    // Drops the optional internal (publisher-side) signatory block
    hideInternalSignatory?: boolean;
    // Email the parent already searched on Docoon to build docoonSignatories (the main signatory's)
    preloadedEmail?: string;
    documentType?: "agreement" | "avenant" | "framework_contract";
};

const DocoonSignatoryStep = ({
    mainSignerEmail,
    mainSignerName,
    docoonSignatories,
    isLoading,
    onSearch,
    onChange,
    onSignatoriesChange,
    initialMain,
    initialCoSelections,
    initialInternal,
    initialExtras,
    initialInformed,
    coBeneficiaries = [],
    onBlockingReasonChange,
    hideInternalSignatory,
    preloadedEmail,
    documentType,
}: Props) => {
    const [t] = useTranslation();

    const LOADING_MESSAGES = [
        t("DocoonSignModal.LoadingMsg1"),
        t("DocoonSignModal.LoadingMsg2"),
        t("DocoonSignModal.LoadingMsg3"),
        t("DocoonSignModal.LoadingMsg4"),
    ];

    const loadingMessage = useProgressiveMessage(isLoading, LOADING_MESSAGES);
    // Seeded with the preload: slots auto-match on their first render, before any merge effect runs
    const [allSignatories, setAllSignatories] = useState<DocoonSignatory[]>(docoonSignatories);
    const [selectedMain, setSelectedMain] = useState<DocoonSignatory | null>(initialMain ?? null);
    const [coSelections, setCoSelections] = useState<Record<number, DocoonSignatory | null>>(
        () => initialCoSelections ?? {},
    );
    const [internalSelected, setInternalSelected] = useState<DocoonSignatory | null>(initialInternal ?? null);
    // Stable keys: extras can be removed from the middle of the list
    const nextExtraKey = useRef(0);
    const [extras, setExtras] = useState<ExtraEntry[]>(
        () => initialExtras?.map((s) => ({ key: nextExtraKey.current++, selected: s })) ?? [],
    );
    const [informed, setInformed] = useState<ExtraEntry[]>(
        () => initialInformed?.map((s) => ({ key: nextExtraKey.current++, selected: s })) ?? [],
    );

    // Merge without duplicates — fed by both the pre-loaded list and remote searches
    const mergeSignatories = (list: DocoonSignatory[]) =>
        setAllSignatories((prev) => {
            const seen = new Set(prev.map((s) => s.id));
            const toAdd = list.filter((s) => !seen.has(s.id));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });

    useEffect(() => {
        mergeSignatories(docoonSignatories);
    }, [docoonSignatories]);

    const groups = useMemo(() => groupByEmail(allSignatories), [allSignatories]);

    // The parent rebuilds coBeneficiaries on every render: key memos on the ids, not the array
    const coBeneficiaryKey = coBeneficiaries.map((b) => b.beneficiaryId).join(",");
    const coSelected = useMemo(
        () => coBeneficiaries.map((b) => coSelections[b.beneficiaryId] ?? null),
        [coBeneficiaryKey, coSelections],
    );

    const selectedIds = useMemo(() => {
        const ids = new Set<number>();
        if (selectedMain) ids.add(selectedMain.id);
        coSelected.forEach((s) => s && ids.add(s.id));
        if (internalSelected) ids.add(internalSelected.id);
        extras.forEach((e) => e.selected && ids.add(e.selected.id));
        informed.forEach((e) => e.selected && ids.add(e.selected.id));
        return ids;
    }, [selectedMain, coSelected, internalSelected, extras, informed]);
    const claimedIds = useRef<Set<number>>(new Set());

    // Propagate selection to parent. The first signatory is sent as the main one, and every
    // co-beneficiary picked for a multi-signature contract must sign: nothing is propagated until
    // all of them are matched (some may be auto-matched before the main one).
    useEffect(() => {
        const missingCoNames = coBeneficiaries.filter((_, i) => !coSelected[i]).map((b) => b.name);
        const blockingReason = !selectedMain
            ? t("DocoonSignModal.MissingMainSignatory")
            : missingCoNames.length > 0
              ? t("DocoonSignModal.MissingCoBeneficiarySignatories", { names: missingCoNames.join(", ") })
              : null;
        const coWithRole = coSelected.map((s, i) => s && { ...s, beneficiaryId: coBeneficiaries[i].beneficiaryId });
        const internalWithRole = internalSelected && { ...internalSelected, isInternal: true };
        const informedWithRole = informed.map((e) => e.selected && { ...e.selected, isInformed: true });
        const signers = blockingReason
            ? []
            : ([selectedMain, ...coWithRole, internalWithRole, ...extras.map((e) => e.selected)].filter(
                  Boolean,
              ) as DocoonSignatoryWithRole[]);
        const selected = blockingReason
            ? []
            : [...signers, ...(informedWithRole.filter(Boolean) as DocoonSignatoryWithRole[])];
        onChange(signers.map((s) => s.id).join(","));
        onSignatoriesChange?.(selected);
        onBlockingReasonChange?.(blockingReason);
    }, [selectedMain, coSelected, internalSelected, extras, informed]);

    const selectEntry = (key: number, s: DocoonSignatory) => (prev: ExtraEntry[]) =>
        prev.map((ex) => (ex.key === key ? { ...ex, selected: s } : ex));
    const removeEntry = (key: number) => (prev: ExtraEntry[]) => prev.filter((ex) => ex.key !== key);
    const addEntry = (prev: ExtraEntry[]) => [...prev, { key: nextExtraKey.current++, selected: null }];

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-sm text-[#646464]">
                <Spinner size={48} />
                {loadingMessage}
            </div>
        );
    }

    // Sibling slots auto-match in effects of the same commit, so they all see the same (stale)
    // selectedIds. This set is rebuilt from state on each render and updated synchronously when a
    // slot auto-picks, so two slots sharing an email can't grab the same identity.
    claimedIds.current = new Set(selectedIds);

    const slotProps = {
        allSignatories,
        groups,
        selectedIds,
        claimedIds: claimedIds.current,
        onSearch,
        onResults: mergeSignatories,
        t,
    };

    const canAdd = !!selectedMain;
    const hasParticipants = extras.length > 0 || informed.length > 0;
    // One empty field at a time per block: fill it (or remove it) before adding another
    const hasEmptyEntry = (entries: ExtraEntry[]) => entries.some((e) => !e.selected);

    return (
        <div className="flex flex-col gap-4">
            <p className="m-0 text-sm text-[#6b7280]">{t("DocoonSignModal.StepDescription")}</p>

            {/* Scrolls past a few blocks so the "+" links and the footer buttons stay in view */}
            <div className="flex flex-col gap-6 max-h-[50vh] overflow-y-auto -mx-1 px-1 py-0.5">
                {/* Main signatory */}
                <div className="flex flex-col gap-2">
                    <div className="flex items-baseline justify-between">
                        <label className="text-sm font-medium text-[#646464]">
                            {t("DocoonSignModal.MainSignatoryLabel")}
                        </label>
                        <span className="text-xs text-[#646464]">
                            {t(
                                documentType === "avenant"
                                    ? "DocoonSignModal.BeneficiaryOfAvenant"
                                    : "DocoonSignModal.BeneficiaryOf",
                                { name: mainSignerName },
                            )}
                        </span>
                    </div>

                    <BeneficiarySignatorySlot
                        {...slotProps}
                        email={mainSignerEmail}
                        isMain
                        emailAlreadySearched={
                            !!preloadedEmail && preloadedEmail.toLowerCase() === mainSignerEmail.toLowerCase()
                        }
                        selected={selectedMain}
                        onSelect={setSelectedMain}
                    />
                </div>

                {/* Co-beneficiaries of a multi-signature contract: always required */}
                {coBeneficiaries.map((b, i) => (
                    <div key={`beneficiary-${b.beneficiaryId}`} className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#646464]">
                            {t("DocoonSignModal.BeneficiaryOf", { name: b.name })} *
                        </label>
                        <BeneficiarySignatorySlot
                            {...slotProps}
                            email={b.email}
                            isMain={false}
                            selected={coSelected[i]}
                            onSelect={(s) => setCoSelections((prev) => ({ ...prev, [b.beneficiaryId]: s }))}
                        />
                    </div>
                ))}

                {/* Internal signatory (optional) */}
                {!hideInternalSignatory && (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-baseline justify-between gap-4">
                            <div className="flex flex-col gap-0.5">
                                <label className="text-sm font-medium text-[#646464]">
                                    {t("DocoonSignModal.InternalSignatoryLabel")}
                                </label>
                                <span className="text-xs text-[#6b7280]">
                                    {t("DocoonSignModal.InternalSignatoryHint")}
                                </span>
                            </div>
                            <span className="text-xs text-[#6b7280]">
                                {t("DocoonSignModal.InternalSignatoryOptional")}
                            </span>
                        </div>
                        <EntryField
                            {...slotProps}
                            entry={{ key: 0, selected: internalSelected }}
                            onSelect={setInternalSelected}
                            onRemove={() => setInternalSelected(null)}
                            autoFocus={false}
                        />
                    </div>
                )}

                {/* Other participants (optional): extra signatories first, informed people last, as sent */}
                {hasParticipants && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#646464]">
                            {t("DocoonSignModal.OtherParticipantsLabel")}
                        </label>
                        {extras.map((ex) => (
                            <EntryField
                                key={ex.key}
                                {...slotProps}
                                role="signatory"
                                entry={ex}
                                onSelect={(s) => setExtras(selectEntry(ex.key, s))}
                                onRemove={() => setExtras(removeEntry(ex.key))}
                            />
                        ))}
                        {informed.map((ex) => (
                            <EntryField
                                key={ex.key}
                                {...slotProps}
                                role="informed"
                                entry={ex}
                                onSelect={(s) => setInformed(selectEntry(ex.key, s))}
                                onRemove={() => setInformed(removeEntry(ex.key))}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <AddEntryButton
                        enabled={canAdd && !hasEmptyEntry(extras)}
                        icon={<AddIcon sx={{ fontSize: 16 }} />}
                        label={t("DocoonSignModal.AddSignatory")}
                        description={t("DocoonSignModal.AddSignatoryDescription")}
                        onClick={() => setExtras(addEntry)}
                    />
                    <AddEntryButton
                        enabled={canAdd && !hasEmptyEntry(informed)}
                        icon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
                        label={t("DocoonSignModal.AddInformed")}
                        description={t("DocoonSignModal.AddInformedDescription")}
                        onClick={() => setInformed(addEntry)}
                    />
                </div>
                {!canAdd ? (
                    <span className="text-xs text-[#6b7280]">{t("DocoonSignModal.AddSignatoryHint")}</span>
                ) : (
                    informed.some((e) => e.selected) && (
                        <span className="text-xs text-[#6b7280]">{t("DocoonSignModal.InformedOrderHint")}</span>
                    )
                )}
            </div>
        </div>
    );
};

export default DocoonSignatoryStep;
